import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ArchiveButton } from "@/components/ArchiveButton";
import { Repeat, Plus, Clock, Building2, Play, X } from "lucide-react";
import { toast } from "sonner";

const PATTERN_LABEL = { harian: "Harian", mingguan: "Mingguan", bulanan: "Bulanan", custom: "Custom" };

export default function Routines() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [positions, setPositions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", responsible_id: "", pattern: "harian", deadline_time: "17:00", priority: "normal", estimated_hours: 1 });
  const [checklist, setChecklist] = useState([""]);
  const canManage = ["super_admin", "owner", "atasan", "kepala_divisi"].includes(user?.role);

  const load = () => api.get("/routines").then((r) => setTemplates(r.data));
  useEffect(() => { load(); api.get("/org/positions").then((r) => setPositions(r.data)); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.responsible_id) return toast.error("Judul & penanggung jawab wajib");
    const pos = positions.find((p) => p.id === form.responsible_id);
    await api.post("/routines", {
      ...form, responsible_type: "posisi", estimated_hours: Number(form.estimated_hours),
      division_id: pos?.division?.id || pos?.division_id, branch_id: pos?.branch?.id || pos?.branch_id,
      checklist: checklist.filter((c) => c.trim()).map((c) => ({ text: c, weight: 0 })),
    });
    toast.success("Template rutin dibuat"); setCreateOpen(false);
    setForm({ title: "", responsible_id: "", pattern: "harian", deadline_time: "17:00", priority: "normal", estimated_hours: 1 });
    setChecklist([""]); load();
  };

  const toggle = async (rid) => { await api.post(`/routines/${rid}/toggle`); load(); };
  const generateNow = async () => { const r = await api.post("/routines/generate-now"); toast.success(`${r.data.created} instance tugas rutin dibuat untuk hari ini`); };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Repeat className="h-7 w-7 text-indigo-500" />Tugas Rutin</h1>
          <p className="text-slate-500 text-sm">Template rutin toko dibuat otomatis oleh scheduler 00:05 WIB</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={generateNow} data-testid="generate-now-btn"><Play className="h-4 w-4 mr-1.5" />Generate Hari Ini</Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="create-routine-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Buat Template</Button>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <div key={t.id} data-testid={`routine-card-${t.id}`}
            className={cn("rounded-2xl border bg-white p-4 shadow-sm", t.is_active ? "border-slate-200" : "border-slate-200 opacity-60")}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold text-slate-800 leading-snug">{t.title}</p>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <Switch checked={t.is_active} onCheckedChange={() => toggle(t.id)} data-testid={`routine-toggle-${t.id}`} />
                  <ArchiveButton url={`/routines/${t.id}`} label="template rutin ini" onDone={load} testid={`archive-routine-${t.id}`} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 font-medium">{PATTERN_LABEL[t.pattern]}</span>
              <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" />Deadline {t.deadline_time}</span>
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{t.branch?.name}</span>
              <span>· {t.responsible_label}</span>
            </div>
            {t.checklist?.length > 0 && <p className="text-xs text-slate-400 mt-2">{t.checklist.length} langkah checklist</p>}
            <p className="text-xs text-slate-400 mt-1">{t.instances_count} instance dibuat</p>
          </div>
        ))}
        {templates.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">Belum ada template rutin.</p>}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto zoa-scroll">
          <DialogHeader><DialogTitle>Buat Template Rutin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Judul</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="routine-title-input" placeholder="Opening Toko" /></div>
            <div><Label>Penanggung Jawab (Posisi)</Label>
              <Select value={form.responsible_id} onValueChange={(v) => setForm((f) => ({ ...f, responsible_id: v }))}>
                <SelectTrigger data-testid="routine-responsible-select"><SelectValue placeholder="Pilih posisi" /></SelectTrigger>
                <SelectContent>{positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Pola</Label>
                <Select value={form.pattern} onValueChange={(v) => setForm((f) => ({ ...f, pattern: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="harian">Harian</SelectItem><SelectItem value="mingguan">Mingguan</SelectItem><SelectItem value="bulanan">Bulanan</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Jam Deadline</Label><Input type="time" value={form.deadline_time} onChange={(e) => setForm((f) => ({ ...f, deadline_time: e.target.value }))} /></div>
              <div><Label>Est. Jam</Label><Input type="number" step="0.5" value={form.estimated_hours} onChange={(e) => setForm((f) => ({ ...f, estimated_hours: e.target.value }))} /></div>
            </div>
            <div>
              <Label className="mb-1.5 block">Checklist Standar</Label>
              {checklist.map((c, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <Input value={c} onChange={(e) => setChecklist((a) => a.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Langkah ${i + 1}`} />
                  {checklist.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setChecklist((a) => a.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setChecklist((a) => [...a, ""])}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Batal</Button><Button onClick={create} data-testid="routine-create-submit" className="bg-indigo-600 hover:bg-indigo-700">Buat</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
